import { gql } from "@apollo/client";
import { Alert, Skeleton } from "@mui/material";
import { alpha } from "@mui/material";
import DateTime from "atoms/DateTime";
import { PageContentStack } from "atoms/Stack";
import Stack from "atoms/Stack";
import { MAX_CONTENT_WIDTH } from "helpers/breakpoints";
import { COLOR_BLACK, COLOR_WHITE_LIGHTER } from "helpers/colors";
import { SPACING } from "helpers/spacings";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import useRouteState from "helpers/useRouteState";
import useSearchTerm from "helpers/useSearchTerm";
import Subnav, { SubnavDropdown, SubnavDropdownItem, SubnavDropdownSearchField } from "molecules/Subnav";
import React, { useLayoutEffect } from "react";
import NotFoundPage from "shared/NotFoundPage";
import PageTitle from "shared/PageTitle";

import LeaderboardTableSegment, { LeaderboardTableSegmentFragment } from "./leaderboard/LeaderboardTableSegment";

export default function LeaderboardsPage() {
  useEnforceAuth({ enforceAuthenticated: true });

  const [seasonId, seasonIdSet] = useRouteState("seasonId");
  const [leagueSearchTerm, leagueSearchTermSet, leagueSearchTermDebounced] = useSearchTerm();
  const [data] = useData(
    gql`
      query LeaderboardsPage($seasonId: ID, $leagueSearchTerm: String!) {
        season(id: $seasonId) {
          id
          nameWithLeague
          leaderboardTableSegments {
            title
            ...LeaderboardTableSegmentFragment
          }
          league {
            id
            sport
          }
        }
        currentUser {
          id
          gamesSeasons(searchTerm: $leagueSearchTerm, limit: 20) {
            id
            nameWithLeague
            firstPlayedAt
            lastPlayedAt
          }
        }
      }
      ${LeaderboardTableSegmentFragment}
    `,
    { seasonId, leagueSearchTerm: leagueSearchTermDebounced },
  );

  useLayoutEffect(() => {
    leagueSearchTermSet("");
  }, [seasonId]);

  if (data && !data.season) return <NotFoundPage />;

  return (
    <>
      <PageTitle title="Leaderboards" />
      <div
        style={{
          backgroundColor: COLOR_WHITE_LIGHTER,
          borderBottom: `1px solid ${alpha(COLOR_BLACK, 0.1)}`,
        }}
      >
        <Subnav wrap={false}>
          <SubnavDropdown text={data?.season?.nameWithLeague}>
            <SubnavDropdownSearchField searchTerm={leagueSearchTerm} searchTermSet={leagueSearchTermSet} />
            {data?.currentUser?.gamesSeasons.map((season) => (
              <SubnavDropdownItem
                key={season.id}
                text={season.nameWithLeague}
                onClick={() => seasonIdSet(season.id)}
                selected={season.id === seasonId}
                description={
                  <>
                    <DateTime value={season.firstPlayedAt} variant="month" /> -{" "}
                    <DateTime value={season.lastPlayedAt} variant="month" />
                  </>
                }
              />
            ))}
          </SubnavDropdown>
        </Subnav>
      </div>
      <PageContentStack>
        <Stack
          gap={SPACING}
          style={{ margin: "0 auto", maxWidth: MAX_CONTENT_WIDTH, width: `calc(100vw - ${SPACING} * 2)` }}
        >
          {!data && <Skeleton variant="rectangular" height={200} />}
          {data && data.season?.league?.sport === "pickleball" && (
            <Alert variant="outlined" severity="info" icon={false}>
              <p>Leaderboard statistics are not available for Pickleball at this time.</p>
            </Alert>
          )}
          {data && !data.season?.leaderboardTableSegments?.length && (
            <Alert variant="outlined" severity="info" icon={false}>
              <p>Leaderboards are not yet available. Change filter to view other leaderboards.</p>
            </Alert>
          )}
          {data &&
            data.season?.league?.sport !== "pickleball" &&
            data.season?.leaderboardTableSegments?.map((leaderboardTableSegment) => (
              <LeaderboardTableSegment
                key={leaderboardTableSegment.title}
                leaderboardTableSegment={leaderboardTableSegment}
                collapsible={true}
              />
            ))}
        </Stack>
      </PageContentStack>
    </>
  );
}
